import React, { useCallback, useMemo } from 'react';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useDispatch, useSelector } from 'react-redux';
import MapView from '../map/core/MapView';
import MapSelectedDevice from '../map/main/MapSelectedDevice';
import MapAccuracy from '../map/main/MapAccuracy';
import MapGeofence from '../map/MapGeofence';
import PoiMap from '../map/main/PoiMap';
import MapPadding from '../map/MapPadding';
import { devicesActions } from '../store';
import MapDefaultCamera from '../map/main/MapDefaultCamera';
import MapLiveRoutes from '../map/main/MapLiveRoutes';
import MapPositions from '../map/MapPositions';
import MapOverlay from '../map/overlay/MapOverlay';
import MapGeocoder from '../map/geocoder/MapGeocoder';
import MapScale from '../map/MapScale';
import MapNotification from '../map/notification/MapNotification';
import useFeatures from '../common/util/useFeatures';
import MapCurrentLocation from '../map/MapCurrentLocation';
import MapAgentLocation from '../map/main/MapAgentLocation';
import { useAdministrator } from '../common/util/permissions';

const MainMap = ({
  filteredPositions, selectedPosition, selectedMarker, onEventsClick,
}) => {
  const theme = useTheme();
  const dispatch = useDispatch();

  const desktop = useMediaQuery(theme.breakpoints.up('md'));

  const eventsAvailable = useSelector((state) => !!state.events.items.length);

  const features = useFeatures();
  const admin = useAdministrator();

  const onMarkerClick = useCallback((_, deviceId) => {
    dispatch(devicesActions.select(deviceId));
  }, [dispatch]);

  // The accuracy circle is a 64-point polygon per position. Drawing one for
  // every device on a large fleet was the most expensive step of each flush,
  // and a circle only means something for the vehicle the user is looking at.
  const accuracyPositions = useMemo(
    () => (selectedPosition ? [selectedPosition] : []),
    [selectedPosition],
  );

  return (
    <>
      <MapView>
        <MapOverlay />
        <MapGeofence />
        <MapAccuracy positions={accuracyPositions} />
        <MapLiveRoutes />
        {/* The dots are drawn from the marker index, which carries the
            device fields the layer needs, so the selected one is matched
            by its marker rather than its full position. */}
        <MapPositions
          positions={filteredPositions}
          onClick={onMarkerClick}
          selectedPosition={selectedMarker}
          showStatus
        />
        <MapDefaultCamera />
        <MapSelectedDevice />
        <PoiMap />
        <MapAgentLocation />
        {/* Locate-me is for anyone on a phone, where it is a real GPS fix;
            on a desktop it is a rough network guess, so it stays an admin tool. */}
        {(!desktop || admin) && <MapCurrentLocation />}
      </MapView>
      <MapScale />
      <MapGeocoder />
      {!features.disableEvents && eventsAvailable && (
        <MapNotification enabled={eventsAvailable} onClick={onEventsClick} />
      )}
      {desktop && (
        <MapPadding left={parseInt(theme.dimensions.drawerWidthDesktop, 10)} />
      )}
    </>
  );
};

export default MainMap;
